import React, { useMemo } from "react";
import { CssBaseline } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Palette from "./palette";
import Typography from "./typography";
import { CustomInfoIcon } from "../components/CustomIcons";
import { cssVariables, tokens } from "./tokens";

type Props = {
  children: JSX.Element;
};

export const mainColorHover = "rgba(43, 178, 76, 0.06)";
export const backgroundActiveElement = "rgba(43, 178, 76, 0.10)";

// Below this width, dropdowns (Select/Autocomplete) switch to mobile-friendly
// sizing: 16px input font (prevents iOS Safari auto-zoom on focus) and
// taller touch targets. Matches the app's existing "md" layout breakpoint.
export const mobileDropdownQuery = "@media (max-width:899.95px)";

export default function ThemeCustomization({ children }: Props) {
  const themes = useMemo(() => {
  const themePalette = Palette();
  const themeTypography = Typography();

  const components = {
    MuiCssBaseline: {
      styleOverrides: {
        ":root": cssVariables,
        html: {
          backgroundColor: tokens.bg,
        },
        body: {
          backgroundColor: tokens.bg,
          color: tokens.ink,
        },
        "#root": {
          backgroundColor: tokens.bg,
          minHeight: "100vh",
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        align: "left" as const,
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          width: "24px",
          height: "24px",
          borderRadius: "6px",
          cursor: "pointer",
          color: "#555555",
          transition: "color 0.15s ease, background 0.15s ease",

          "&:hover:not(.no-hover-icon)": {
            background: mainColorHover,
            color: themePalette.palette.primary.main,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--line)",
          },
          "&:hover:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
            borderColor: themePalette.palette.primary.main,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: "2px",
          },
          // Prevents iOS Safari from auto-zooming the page when a dropdown's
          // text input is focused (it zooms whenever font-size is under 16px).
          [mobileDropdownQuery]: {
            "& input, & .MuiSelect-select": {
              fontSize: "16px",
            },
          },
        },
      },
      defaultProps: {
        size: "small" as const,
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          transform: "none",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          borderRadius: "8px",
          border: `1px solid ${tokens.line}`,
        },
      },
      defaultProps: {},
    },
    // The header is a Paper too, but a full-width bar, not a card.
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: "none",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#1b1c1c",
          color: "#f0f0f0",
          fontSize: "12px",
          borderRadius: "6px",
          padding: "6px 12px",
        },
        arrow: {
          color: "#1b1c1c",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          border: "none",
          fontSize: "14px",
          color: tokens.ink,
        },

        standardInfo: {
          "& .MuiAlert-icon": {
            display: "flex",
            alignItems: "center",
          },
        },
      },
      defaultProps: {
        iconMapping: {
          success: <CustomInfoIcon />,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          "&:hover": {
            backgroundColor: mainColorHover,
          },
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: backgroundActiveElement,
          },
          "&.Mui-focusVisible": {
            outline: `2px solid ${themePalette.palette.primary.main}`,
            outlineOffset: "2px",
          },
          "&.Mui-selected.Mui-focusVisible": {
            outline: "none",
          },
          // Bigger tap target for dropdown options on phones/tablets.
          [mobileDropdownQuery]: {
            minHeight: "44px",
          },
        },
      },
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          "&:hover": {
            backgroundColor: mainColorHover,
          },
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: backgroundActiveElement,
          },
          "&.Mui-focusVisible": {
            outline: `2px solid ${themePalette.palette.primary.main}`,
            outlineOffset: "2px",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": {
            outline: `2px solid ${themePalette.palette.primary.main}`,
            outlineOffset: "2px",
          },
        },
      },
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small" as const,
        fullWidth: true,
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: "small" as const,
        fullWidth: true,
      },
    },
    MuiSelect: {
      defaultProps: {
        size: "small" as const,
        MenuProps: {
          disableScrollLock: true,
          PaperProps: { sx: { maxHeight: 320 } },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: "small" as const,
      },
      styleOverrides: {
        listbox: {
          maxHeight: 320,
          [mobileDropdownQuery]: {
            "& .MuiAutocomplete-option": {
              minHeight: "44px",
            },
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          padding: "10px 16px",
          boxShadow: "none",
          borderRadius: "8px",
          textTransform: "none" as const,
          fontWeight: 500,
          fontSize: "14px",
          "&:hover": {
            boxShadow: "none",
          },
          "&.Mui-focusVisible": {
            outline: `2px solid ${themePalette.palette.primary.main}`,
            outlineOffset: "2px",
          },
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          "&.Mui-focusVisible": {
            outline: `2px solid ${themePalette.palette.primary.main}`,
            outlineOffset: "2px",
          },
        },
        clickable: {
          "&:hover": {
            backgroundColor: mainColorHover,
          },
        },
      },
    },
  };

  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 800,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    direction: "ltr",
    palette: themePalette.palette,
    typography: themeTypography.typography,
    zIndex: {
      appBar: 1200,
    },
    components,
  });
  }, []);

  return (
    <ThemeProvider theme={themes}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}